import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useState } from "react"

export const RatingSurvey = ({onClose}) => {
    const [pageNumber,setPageNumber] = useState(0);
    const [ratingCounter,setRatingCounter] = useState({});
    const [ratingTotal,setRatingTotal] = useState();
    const [pioneerBonus, setPioneerBonus] = useState(0.0);
    const [flawPenalty, setFlawPenalty] = useState(0.0);

    const questionData = [
    {
        id: "gameplay",
        title: "Mechanical Execution & Game Feel",
        weight: 0.7,
        questions: [
        { id: "g1", text: "How responsive, intuitive, and satisfying do the controls and player actions feel?", allowNA: false },
        { id: "g2", text: "How fun, engaging, and well-designed is the moment-to-moment core gameplay loop?", allowNA: false },
        { id: "g3", text: "How well-crafted are the levels, encounter areas, and environments in supporting the gameplay?", allowNA: false },
        { id: "g4", text: "How effectively does the game onboard the player and communicate its systems, rules, and feedback?", allowNA: false },
        { id: "g5", text: "How well-balanced is the difficulty curve, offering fair mastery rather than artificial punishment?", allowNA: false },
        { id: "g6", text: "How well-paced is the introduction of new mechanics, upgrades, and systems throughout the game?", allowNA: false },
        { id: "g7", text: "How satisfying and mechanically meaningful are the rewards for milestones or progression checks?", allowNA: false },
        { id: "g8", text: "How effectively does the game accommodate player agency, strategic freedom, or creative problem-solving?", allowNA: true },
        { id: "g9", text: "How smart, believable, and fair is the artificial intelligence of enemies and companions?", allowNA: true },
        ],
    },
    {
        id: "technical",
        title: "Technical Integrity, Performance & UX",
        weight: 0.5,
        questions: [
        { id: "t1", text: "How stable is the game in maintaining smooth performance, without crashes, severe frame drops, or game-breaking bugs?", allowNA: false },
        { id: "t2", text: "How clean, polished, and visually stable is the graphical presentation (free of pop-in, visual glitches, or blurry rendering)?", allowNA: false },
        { id: "t3", text: "How readable, responsive, and ergonomically designed are the HUD, menus, and user interfaces?", allowNA: false },
        { id: "t4", text: "How clear, legible, and customizable are the subtitles, menus, and on-screen text?", allowNA: true },
        { id: "t5", text: "How comprehensive and flexible are the accessibility options, button remapping, and gameplay settings?", allowNA: false },
        { id: "t6", text: "How reliable and clear is the camera during intense or critical gameplay?", allowNA: false },
        { id: "t7", text: "How well-optimized is the game for its platform, including hardware performance, load times, and controller support?", allowNA: false },
        ],
    },
    {
        id: "artistic",
        title: "Artistic, Audio & Tonal Cohesion",
        weight: 0.5,
        questions: [
        { id: "a1", text: "How distinctive, cohesive, and intentional is the overall art style and visual direction?", allowNA: false },
        { id: "a2", text: "How crisp, impactful, and communicative are the sound effects during gameplay?", allowNA: false },
        { id: "a3", text: "How effectively does the musical score elevate the emotional tone, pacing, and identity of the world?", allowNA: true },
        { id: "a4", text: "How compelling, well-paced, and emotionally engaging is the primary plot, character writing, and dialogue?", allowNA: true },
        { id: "a5", text: "How rich, imaginative, and cohesive is the world-building and lore?", allowNA: true },
        { id: "a6", text: "How convincingly does the vocal delivery or character dialogue performance complement the tone?", allowNA: true },
        { id: "a7", text: "How well do the game's story themes and world align with the player's actual in-game actions?", allowNA: false },
        ],
    },
    {
        id: "value",
        title: "Value Proposition & Content Scope",
        weight: 0.3,
        questions: [
        { id: "v1", text: "Does the game offer enough quality content and polish to justify its launch price?", allowNA: false },
        { id: "v2", text: "How well does the game respect your time, avoiding unnecessary filler, grind, and bloated checklists?", allowNA: false },
        { id: "v3", text: "How much meaningful replay value, build variety, or long-term appeal does the game offer?", allowNA: false },
        ],
    },
    {
        id: "multiplayer",
        title: "Multiplayer & Live-Service Logistics",
        isConditional: true,
        questions: [
        { id: "m1", text: "How stable and responsive is the online experience (connection stability, latency, and hit registration)?", allowNA: true, targetPillar: "technical" },
        { id: "m2", text: "How fair, fast, and accessible are matchmaking queues, lobby systems, anti-cheat, and cross-play features?", allowNA: true, targetPillar: "technical" },
        { id: "m3", text: "How diverse and balanced are the different roles, characters, weapons, or playstyles against dominant metas?", allowNA: true, targetPillar: "gameplay" },
        { id: "m4", text: "How mechanically reliable and rewarding is the emergent cooperation or competition between human players?", allowNA: true, targetPillar: "gameplay" },
        { id: "m5", text: "How effective are player reporting, communication tools, and grief-prevention systems?", allowNA: true, targetPillar: "technical" },
        { id: "m6", text: "How fair, unobtrusive, and non-predatory is the monetization (avoiding pay-to-win mechanics or artificially throttled progression)?", allowNA: true, targetPillar: "value" },
        { id: "m7", text: "How impactful, consistent, and well-executed are live balance adjustments and post-launch content updates?", allowNA: true, targetPillar: "value" },
        ],
    },
    {
        id: "modifiers",
        title: "Outlier & Innovation Modifiers",
        isModifier: true,
        questions: [
        { id: "pioneerBonus", text: "Does the game introduce an industry-defining innovation, revolutionary mechanic, or new benchmark for gaming?" },
        { id: "flawPenalty", text: "Does the game suffer from a fundamental, critical design flaw that severely undermines an otherwise functional foundation?" },
        ],
    },
    ];
    const modifierData = questionData.find((c) => c.id === "modifiers");

    const addValue = (index,value) => {
        setRatingCounter((prev) => ({ ...prev, [index]: value }));
    }

    const calculateRating = () => {
        const scoresByPillar = {
            gameplay: [],
            technical: [],
            artistic: [],
            value: [],
        };

        questionData.forEach((cat) => {
            if (!cat.isConditional && !cat.isModifier) {
                cat.questions.forEach((q) => {
                    const val = ratingCounter[q.id];
                        if (val && val !== -1) {
                            scoresByPillar[cat.id].push(Number(val));
                        }
                });
            }
        });

        const mpCategory = questionData.find((c) => c.id === "multiplayer");
        if (mpCategory) {
            mpCategory.questions.forEach((q) => {
                const val = ratingCounter[q.id];
                    if (val && val !== -1) {
                        scoresByPillar[q.targetPillar].push(Number(val));
                    }
            });
        }

        const getAvg = (arr) => arr.reduce((acc, curr) => acc + curr, 0) / arr.length;

        const p1 = getAvg(scoresByPillar.gameplay);
        const p2 = getAvg(scoresByPillar.technical);
        const p3 = getAvg(scoresByPillar.artistic);
        const p4 = getAvg(scoresByPillar.value);

        let computed = 0.7 * p1 + 0.5 * p2 + 0.5 * p3 + 0.3 * p4 + Number(pioneerBonus) - Number(flawPenalty);
        const clamped = Math.min(10.0, Math.max(1.0, Math.round(computed * 2) / 2));
        setRatingTotal(clamped.toFixed(1));
    }

    const close = () => {
        setPageNumber(0);
        setRatingCounter({});
        setRatingTotal();
        onClose();
    }

    const categoryWeight = (() => {
        switch (pageNumber) {
            case 0:
                return "35%"
            
            case 1:
                
                return "25%";

            case 2:
                
                return "25%";

            case 3:
                
                return "15%";
        
            default:
                return "";
        }
    })();

    return(
        <div id="updateEventModal" style={{top:"50%",left:"50%",transform:"translate(-50%,-55%)",textShadow:"1px 1px 2px black", boxShadow:"2px 2px 20px rgba(100, 180, 250)",color:"white"}}>
            <button onClick={close} style={{position:"absolute",top:"10px",right:"10px",paddingTop:"0px",paddingBottom:"1px",paddingRight:"5px",paddingLeft:"5px"}} className="btn btn-danger"><i className="fa fa-close"></i></button>
            <h3 style={{textAlign:"center",textDecoration:"underline"}}>Rating Survey</h3>
            <h6 style={{textAlign:"center"}}><i>Complete All Questions To Calculate Rating.</i></h6>
            {(pageNumber !== questionData.length-1 && pageNumber !== questionData.length-2) ? <h4>{questionData[pageNumber].title} ~ {categoryWeight} of Overall Rating</h4> : <h4>{questionData[pageNumber].title}</h4>}
            <div style={{height:"60%"}}>
                {pageNumber !== questionData.length-1 ? questionData[pageNumber].questions.map((q) => {
                    return(
                        <div key={q.id}>
                            <div>{q.text}</div>
                            <RadioGroup row value={ratingCounter[q.id] ? ratingCounter[q.id] : ""} onChange={(e)=>addValue(q.id,Number(e.target.value))}>
                                {q.allowNA && <FormControlLabel value={-1} control={<Radio />} label="N/A" />}
                                <FormControlLabel value={1} control={<Radio />} label="1 - Horrible" />
                                <FormControlLabel value={2} control={<Radio />} label="2 - Below Average" />
                                <FormControlLabel value={3} control={<Radio />} label="3 - Average" />
                                <FormControlLabel value={4} control={<Radio />} label="4 - Above Average" />
                                <FormControlLabel value={5} control={<Radio />} label="5 - Perfect" />
                            </RadioGroup>
                        </div>
                    )
                })
                :
                    <div>
                        <div>{modifierData.questions[0].text} (+0.0 to +1.0)</div>
                        <div>Current Value: <span>+{pioneerBonus}</span></div>
                        <input type="range" min="0" max="1.0" step="0.1" value={pioneerBonus} onChange={(e) => setPioneerBonus(e.target.value)} style={{ width: "100%" }}/>
                        <div>{modifierData.questions[1].text} (+0.0 to +1.0)</div>
                        <div>Current Value: <span>-{flawPenalty}</span></div>
                        <input type="range" min="0" max="1.0" step="0.1" value={flawPenalty} onChange={(e) => setFlawPenalty(e.target.value)} style={{ width: "100%" }}/>
                    </div>
                }
            </div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
                <button className="btn btn-primary btn-sm" onClick={()=>{setPageNumber(pageNumber-1)}} disabled={pageNumber === 0}>Prev {pageNumber !== 0 && "("+questionData[pageNumber-1].title+")"}</button>
                <button className="btn btn-primary btn-sm" onClick={()=>{setPageNumber(pageNumber+1)}} disabled={pageNumber === questionData.length-1}>Next {pageNumber !== questionData.length-1 && "("+questionData[pageNumber+1].title+")"}</button>
            </div>
            <div style={{textAlign:"center"}}>
                {ratingTotal && <h2>{ratingTotal} / 10</h2>}
                <button className="btn btn-success" onClick={calculateRating} disabled={Object.keys(ratingCounter).length !== 33}>Calculate Rating</button>
            </div>
        </div>
    )
}

export default RatingSurvey;